import {inject, Pipe, PipeTransform} from '@angular/core';
import {LinkifyIt, type Match} from 'linkify-it';
import {environment} from '../../../environments/environment';
import {mapRichTextInlineStylesToClasses} from '../mapper/rich-text-class-mapper';
import {LanguageService} from '../services/language.service';

@Pipe({
  name: 'linkify',
  standalone: true
})
export class LinkifyPipe implements PipeTransform {
  private readonly tooltipUrlCharacterLimit: number = 250;
  private lang: LanguageService = inject(LanguageService);

  private linkify: LinkifyIt = new LinkifyIt({fuzzyLink: false, urlAuth: true});

  public transform(value: string): string {
    if (!value) {
      return value;
    }

    let result = this.normalizeUnderlineStyles(value);
    result = mapRichTextInlineStylesToClasses(result);
    const matches = this.linkify.match(result);

    if (!matches) {
      return result;
    }

    matches.slice().reverse().forEach((match: Match) => {
      const url = match.url;
      const tooltipText = this.escapeAttribute(this.truncateUrlForTooltip(url));
      const anchorTag = `<a href="${this.escapeAttribute(url)}" target="${this.targetResolver(url)}" rel="noopener noreferrer" class="${this.resolveAnchorClassNames(url)}" title="${tooltipText}" aria-label="${tooltipText}">${this.prettifyUrl()}</a>`;
      result = `${result.slice(0, match.index)}${anchorTag}${result.slice(match.lastIndex)}`;
    });

    return result;
  }

  private prettifyUrl(): string {
    return this.lang.getTranslationByKey('OPENLINK');
  }

  private truncateUrlForTooltip(url: string): string {
    return url.slice(0, this.tooltipUrlCharacterLimit).trimEnd();
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private normalizeUnderlineStyles(html: string): string {
    return html.replace(
      /<span\b([^>]*?)\sstyle=(['"])([^'"]*text-decoration\s*:\s*underline[^'"]*)\2([^>]*)>([\s\S]*?)<\/span>/gi,
      '<u$1$4>$5</u>'
    );
  }

  private extractLegacyRoute(url: string): string | null {
    const normalizedLegacyFrontendUrl = this.normalizeLegacyFrontendUrl();
    const [urlWithoutHash, hashFragment = ''] = url.split('#', 2);
    const [urlPath, searchQuery = ''] = urlWithoutHash.split('?', 2);
    const routeSuffix = `${searchQuery ? `?${searchQuery}` : ''}${hashFragment ? `#${hashFragment}` : ''}`;

    if (urlPath === normalizedLegacyFrontendUrl || urlPath === `${normalizedLegacyFrontendUrl}/`) {
      return `/${routeSuffix}`;
    }

    if (!urlPath.startsWith(`${normalizedLegacyFrontendUrl}/`)) {
      return null;
    }

    return `${urlPath.slice(normalizedLegacyFrontendUrl.length)}${routeSuffix}`;
  }

  private normalizeLegacyFrontendUrl(): string {
    return environment.legacyFrontend.url.trim().replace(/\/$/, '');
  }

  private resolveAnchorClassNames(url: string): string {
    return this.extractLegacyRoute(url)
      ? 'linkified linkified-legacy'
      : 'linkified';
  }

  private targetResolver(url: string):string {
    if(url.includes('.humanstars.app')) {
      return '_self';
    } else {
      return '_blank';
    }
  }
}
